import { autoinject } from 'aurelia-dependency-injection';
import { I18N } from 'aurelia-i18n';
import { Router } from 'aurelia-router';

import { AppRouteID } from '~/app-routes';
import { Notification } from '~/components/notification/notification';
import {
	CustomisationEditorSelectDialog,
	CustomisationEditorSelectDialogModel,
	CustomisationEditorSelectResult,
} from '~/forms/customisation-editor/customisation-editor-select-dialog';
import { SettingsState } from '~/settings-state/settings-state';
import { DialogUtils } from '~/utils/dialog-utils';

@autoinject
export class CustomisationEditorSelectDialogManager {
	constructor(
		protected readonly i18n: I18N,
		private readonly dialogUtils: DialogUtils,
		private readonly settingsState: SettingsState,
	) {}

	public checkAuthAndOpenCustomisationEditorSelectDialog(
		router: Router,
		model: CustomisationEditorSelectDialogModel,
	): void {
		const user = this.settingsState.state.user;
		if (!user) {
			// Ignore for non-logged in users.
			// We don't want this logic executing on the login page.
			return;
		}
		if (!user.canEditCustomisations) {
			Notification.error(this.i18n.tr('errors.customisation-auth-error'));
			return;
		}
		this.openCustomisationEditorSelectDialog(router, model);
	}

	private openCustomisationEditorSelectDialog(
		router: Router,
		model: CustomisationEditorSelectDialogModel,
	): void {
		this.dialogUtils
			.showCustomDialog(CustomisationEditorSelectDialog, model)
			.whenClosed((response) => {
				if (!response.wasCancelled) {
					const output: CustomisationEditorSelectResult =
						response.output;
					const path = this.generateCustomisationEditorLink(
						router,
						output.formName,
						output.areaName,
						output.customisationName,
						output.functionName,
					);
					window.open(path);
				}
			});
	}

	private generateCustomisationEditorLink(
		router: Router,
		formName: string,
		areaName: string,
		customisationName: string,
		functionName: string,
	): string | undefined {
		if (!router) {
			throw new Error('router is required.');
		}
		if (!formName) {
			throw new Error('formName is required.');
		}
		if (!areaName) {
			throw new Error('areaName is required.');
		}
		const params = router.currentInstruction.queryParams;
		params.formName = formName;
		if (customisationName) {
			params.customisationName = customisationName;
		}
		params.areaName = areaName;
		if (functionName) {
			params.functionName = functionName;
		}
		return router.generate(AppRouteID.CustomisationEditor, params);
	}
}
